/**
 * Modal Component
 * Reusable modal component with backdrop and keyboard navigation
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { type ModalProps } from '../../types';
import { useSafeThemeClasses } from '@/shared/theme';

interface ExtendedModalProps extends ModalProps {
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
}

export const Modal: React.FC<ExtendedModalProps> = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  children,
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
}) => {
  const theme = useSafeThemeClasses();
  
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg', 
    xl: 'max-w-xl',
  };

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, closeOnEscape]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdrop && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Portaled to <body>: position:fixed breaks out of the viewport when the
  // modal is mounted inside a backdrop-blurred page wrapper (theme.background
  // applies backdrop-filter, which establishes a new CSS containing block for
  // fixed descendants), pushing the dialog off-screen instead of centering it.
  return createPortal(
    <div className={`fixed inset-0 z-50 ${theme.modalOverlay}`} onClick={handleBackdropClick}>
      <div className={`relative w-full ${sizeClasses[size]} mx-auto mt-20 p-6 ${theme.modalContent}`}>
        {(title || showCloseButton) && (
          <div className="flex justify-between items-center mb-4">
            {title && <h3 className={`font-bold text-lg ${theme.textHeading}`}>{title}</h3>}
            {showCloseButton && (
              <button
                className={`w-8 h-8 rounded-full flex items-center justify-center ${theme.buttonGhost} ${theme.buttonTextGhost} hover:opacity-75 transition-opacity`}
                onClick={onClose}
                aria-label="Close modal"
              >
                ✕
              </button>
            )}
          </div>
        )}

        <div className="py-4">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
